<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ScenariosAPI from 'dashboard/api/scenarios';
import { eventLabel } from './mres3/eventLabel';
import { scheduleSummary, startMessages } from './mres3/startSchedule';
import en from './mres3/en.json';
import ru from './mres3/ru.json';

const { t: translate } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...startMessages.en },
    ru: { ...ru, ...startMessages.ru },
  },
});

const router = useRouter();
const scenarios = ref([]);
const loading = ref(false);
const search = ref('');

const load = async () => {
  loading.value = true;
  try {
    const { data } = await ScenariosAPI.get();
    scenarios.value = data || [];
  } catch (e) {
    useAlert('Не удалось загрузить сценарии');
  } finally {
    loading.value = false;
  }
};
onMounted(load);

const filtered = computed(() =>
  scenarios.value.filter(s =>
    s.name.toLowerCase().includes(search.value.toLowerCase())
  )
);
const activeCount = computed(
  () => scenarios.value.filter(s => s.status === 'active').length
);
const enrolledTotal = computed(() =>
  scenarios.value.reduce(
    (sum, s) =>
      sum + Object.values(s.enrollments || {}).reduce((a, b) => a + b, 0),
    0
  )
);

const openEditor = id =>
  router.push({ name: 'automation_scenarios_edit', params: { id } });

const createScenario = async () => {
  const name = window.prompt('Имя сценария', 'Новый сценарий');
  if (!name) return;
  try {
    const { data } = await ScenariosAPI.create({
      name,
      trigger: {
        type: 'mres3',
        entity: 'appointments',
        event: 'created',
        connection_id: null,
      },
      graph: {
        version: 1,
        start: null,
        __trigger: { x: 40, y: 160 },
        nodes: {},
      },
    });
    openEditor(data.id);
  } catch (e) {
    useAlert('Не удалось создать сценарий');
  }
};

const toggle = async s => {
  try {
    if (s.status === 'active') await ScenariosAPI.pause(s.id);
    else await ScenariosAPI.activate(s.id);
    await load();
  } catch (e) {
    useAlert('Не удалось изменить статус');
  }
};
const remove = async id => {
  if (!window.confirm('Удалить сценарий?')) return;
  await ScenariosAPI.delete(id);
  await load();
};

const STATUS_LABEL = { active: 'Активен', paused: 'Пауза', draft: 'Черновик' };
const triggerLabel = t => {
  if (!t) return '—';
  if (t.type === 'schedule') return 'По дате клиента';
  if (t.type === 'mres3' && t.mode === 'schedule')
    return `${translate('startSchedule.schedule')} · ${scheduleSummary(t, translate)}`;
  if (t.type === 'mres3') return `MRES3 · ${eventLabel(t.event, translate)}`;
  return (
    {
      DealCreated: 'Создание сделки',
      DealStageChanged: 'Смена стадии сделки',
      DealRescheduled: 'Перенос приёма',
    }[t.operation] || t.operation
  );
};
const enrolledOf = s =>
  Object.values(s.enrollments || {}).reduce((a, b) => a + b, 0);
</script>

<template>
  <div class="w-full h-full overflow-auto bg-n-surface-1">
    <div class="w-full max-w-6xl px-8 py-8 mx-auto">
      <!-- header -->
      <div class="flex items-start justify-between mb-6">
        <div>
          <div class="text-xs font-semibold tracking-wide text-n-brand">
            {{ translate('versionsList.brand') }}
          </div>
          <h1 class="text-2xl font-bold text-n-slate-12">
            {{ translate('versionsList.title') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ translate('listHint') }}
          </p>
        </div>
        <button
          class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
          @click="createScenario"
        >
          {{ translate('versionsList.create') }}
        </button>
      </div>

      <!-- stat cards -->
      <div class="grid grid-cols-2 gap-4 mb-6 md:grid-cols-4">
        <div class="p-4 bg-white border rounded-xl border-n-weak">
          <div class="text-xs text-n-slate-10">
            {{ translate('versionsList.active') }}
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ activeCount }}
          </div>
          <div class="text-xs text-n-slate-10">
            {{ translate('versionsList.total', { count: scenarios.length }) }}
          </div>
        </div>
        <div class="p-4 bg-white border rounded-xl border-n-weak">
          <div class="text-xs text-n-slate-10">
            {{ translate('versionsList.participants') }}
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ enrolledTotal }}
          </div>
          <div class="text-xs text-n-slate-10">
            {{ translate('versionsList.totalEnrollments') }}
          </div>
        </div>
        <div class="p-4 bg-white border rounded-xl border-n-weak">
          <div class="text-xs text-n-slate-10">
            {{ translate('draftCount') }}
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ scenarios.filter(s => s.trigger?.type === 'mres3').length }}
          </div>
          <div class="text-xs text-n-slate-10">{{ translate('preview') }}</div>
        </div>
        <div class="p-4 bg-white border rounded-xl border-n-weak">
          <div class="text-xs text-n-slate-10">
            {{ translate('versionsList.source') }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ translate('sourcePerScenario') }}
          </div>
        </div>
      </div>

      <!-- search -->
      <input
        v-model="search"
        :placeholder="translate('versionsList.search')"
        :aria-label="translate('versionsList.search')"
        class="w-full max-w-sm px-3 py-2 mb-4 text-sm bg-white border rounded-lg border-n-weak text-n-slate-12"
      />

      <!-- table -->
      <div class="overflow-hidden bg-white border rounded-xl border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr
              class="text-xs tracking-wide uppercase border-b border-n-weak text-n-slate-10"
            >
              <th class="px-4 py-3 font-medium text-left">
                {{ translate('versionsList.scenario') }}
              </th>
              <th class="px-4 py-3 font-medium text-left">
                {{ translate('versionsList.status') }}
              </th>
              <th class="px-4 py-3 font-medium text-left">
                {{ translate('versionsList.trigger') }}
              </th>
              <th class="px-4 py-3 font-medium text-right">
                {{ translate('versionsList.members') }}
              </th>
              <th class="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in filtered"
              :key="s.id"
              class="border-b cursor-pointer border-n-weak last:border-0 hover:bg-n-alpha-1"
              @click="openEditor(s.id)"
            >
              <td class="px-4 py-3">
                <div class="font-medium text-n-slate-12">{{ s.name }}</div>
                <p
                  v-if="s.trigger?.type === 'mres3'"
                  class="m-0 mt-1 text-xs text-n-slate-11"
                >
                  {{
                    s.published_version
                      ? translate('versions.number', {
                          number: s.published_version,
                        })
                      : translate('versions.empty')
                  }}
                  <span v-if="s.has_draft">
                    · {{ translate('versions.draftLabel') }}</span
                  >
                </p>
              </td>
              <td class="px-4 py-3">
                <span
                  class="px-2.5 py-0.5 text-xs rounded-full inline-flex items-center gap-1.5"
                  :class="
                    s.status === 'active'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="
                      s.status === 'active' ? 'bg-n-teal-9' : 'bg-n-slate-9'
                    "
                  />
                  {{ STATUS_LABEL[s.status] || s.status }}
                </span>
              </td>
              <td class="px-4 py-3 text-n-slate-11">
                {{ triggerLabel(s.trigger) }}
              </td>
              <td
                class="px-4 py-3 font-medium text-right tabular-nums text-n-slate-12"
              >
                {{ enrolledOf(s) }}
              </td>
              <td class="px-4 py-3 text-right whitespace-nowrap" @click.stop>
                <button
                  v-if="s.trigger?.type !== 'mres3'"
                  class="px-2 py-1 text-xs border rounded border-n-weak text-n-slate-11"
                  @click="toggle(s)"
                >
                  {{ s.status === 'active' ? 'Пауза' : 'Запустить' }}
                </button>
                <button
                  class="px-2 py-1 ml-1 text-xs border rounded border-n-weak text-n-ruby-11"
                  @click="remove(s.id)"
                >
                  {{ translate('versionsList.remove') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div
          v-if="!loading && !filtered.length"
          class="py-10 text-center text-n-slate-11"
        >
          {{ translate('versionsList.empty') }}
        </div>
      </div>
    </div>
  </div>
</template>
